import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'

import {
  FeatureFlagName,
  InitialRequestService,
  IntercomService,
  MeInformationService,
  OwnerRestControllerService,
  PasswordResetRestControllerService,
  ResetPasswordForm,
  TeamMemberRole,
  type UpdateUserSettingsRequest,
  UserSettingsService,
  UserSettingsUiTheme,
} from '@api'
import type { AxiosError } from 'axios'
import { useUnit } from 'effector-react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useIntercom } from 'react-use-intercom'

import { useToast } from '@/components/ui/toast.tsx'
import { LocalStorageKeys, SessionStorageKeys } from '@/consts/localStorage.ts'
import { initializeOpenTelemetry } from '@/metrics/opentelemetry'
import { OTEL_SERVICE_VERSION } from '@/metrics/opentelemetry/consts.ts'
import { setAccount, setIsLoggedIn } from '@/models/account'
import { $availableFeatures } from '@/models/feature-flags'
import { accountService } from '@/modules/accounts/service/account-service.ts'
import { authService } from '@/modules/accounts/service/auth-service.ts'
import { passwordService } from '@/modules/accounts/service/password-service.ts'
import { getPersistedToken } from '@/modules/accounts/utils/token.ts'
import { setUserContext as setSentryUserContext } from '@/modules/sentry/init'
import { SegmentEvents, analytics, identify, onLogout } from '@/utils/analytics'
import { handleError } from '@/utils/error'
import { isFeatureAvailable as isFeatureAvailableUtil } from '@/utils/featureFlags.ts'
import { EVENTS, dispatchCustomEvent } from '@/utils/useEventListener'

import type {
  AccountActivationRequest,
  AccountContext,
  AccountLoginRequest,
  AccountRegisterAfterPaymentRequest,
  AccountRegisterRequest,
  AccountResetPasswordMailRequest,
} from '../types'
import { accountReducer } from './AccountReducer'

export const AccountStateContext = createContext<AccountContext | undefined>(
  undefined,
)

export const AccountProvider = ({ children }: { children: ReactNode }) => {
  const [currentTheme, setCurrentTheme] = useState<UserSettingsUiTheme | null>(
    () =>
      localStorage.getItem(LocalStorageKeys.UI_THEME) as UserSettingsUiTheme,
  )
  const intercom = useIntercom()
  const queryClient = useQueryClient()
  const toast = useToast()
  const featureFlags = useUnit($availableFeatures)
  const isUserFlowFeatureAvailable = isFeatureAvailableUtil(
    featureFlags,
    FeatureFlagName.USER_FLOW,
  )
  const [accountState, dispatchAccountAction] = useReducer(accountReducer, {
    isLoggedIn: undefined,
    checkedAuthentication: false,
    error: null,
    isAccountLoaded: false,
    accessToken: getPersistedToken(LocalStorageKeys.AUTH_TOKEN),
    isManualLogin: false,
    account: null,
    themeData: null,
    userSettings: null,
  })

  const {
    isLoggedIn,
    checkedAuthentication,
    error,
    isAccountLoaded,
    accessToken,
    isManualLogin,
    account,
    themeData,
    userSettings,
  } = accountState

  useEffect(() => {
    if (typeof isLoggedIn === 'boolean') {
      setIsLoggedIn(isLoggedIn)
    }
  }, [isLoggedIn])

  const tokenCheckMutation = useMutation(
    (accessToken: string) => accountService.getTokenInfo(accessToken),
    {
      onError: (error) =>
        handleError(error, 'Something went wrong while checking token'),
    },
  )
  const accountInformationQuery = useQuery(
    'account',
    MeInformationService.findMeInformation,
    {
      onSuccess: async (potentialAccount) => {
        localStorage.setItem(
          LocalStorageKeys.UI_THEME,
          potentialAccount.userSettings!.theme!,
        )
        const sessionData = sessionStorage.getItem(
          SessionStorageKeys.INITIALIZED,
        )

        if (sessionData !== 'true') {
          try {
            await InitialRequestService.init({
              version: OTEL_SERVICE_VERSION,
              appType: 'dashboard',
            })
            sessionStorage.setItem(SessionStorageKeys.INITIALIZED, 'true')
          } catch (error) {
            console.error(error)
          }
        }
        setCurrentTheme(potentialAccount.userSettings!.theme!)

        setAccount(potentialAccount)
        dispatchAccountAction({
          account: potentialAccount,
          isLoggedIn: true,
          checkedAuthentication: true,
          userSettings: potentialAccount.userSettings,
          themeData: {
            chartType: potentialAccount.userSettings!.chartType!,
            theme: potentialAccount.userSettings!.theme!,
          },
        })

        initializeOpenTelemetry(potentialAccount.tracingEnabled)
      },
      onError: (error: AxiosError) => {
        setAccount(null)
        dispatchAccountAction({
          account: null,
          isLoggedIn: false,
          checkedAuthentication: true,
          error,
        })

        if (
          error.response?.status === 403 &&
          accessToken &&
          accessToken.length > 0
        ) {
          tokenCheckMutation.mutateAsync(accessToken).then((status) => {
            if (status === 'valid') {
              return
            }

            if (status === 'invalid') {
              toast.warning(
                'You have been automatically logged out, as 30 days have passed since you logged in, please log in again',
                { duration: 60000 * 60 },
              )
            }

            dispatchAccountAction({
              accessToken: null,
            })
          })
        }
      },
    },
  )

  const currentLogo = useMemo(() => {
    if (account?.customLogoDark || account?.customLogoLight) {
      switch (currentTheme) {
        case UserSettingsUiTheme.DARK:
          return account.customLogoDark
        case UserSettingsUiTheme.LIGHT:
          return account.customLogoLight
        default:
          if (window?.matchMedia?.('(prefers-color-scheme:dark)')?.matches) {
            return account.customLogoDark
          }

          return account.customLogoLight
      }
    }

    return null
  }, [account?.customLogoLight, account?.customLogoDark, currentTheme])

  useQuery(
    ['intercom', featureFlags],
    IntercomService.getIntercomConfiguration,
    {
      enabled:
        !accountInformationQuery.isFetching &&
        !!accountInformationQuery.data?.userId,
      onSuccess: (response) => {
        intercom.boot({
          userId: response.userId,
          email: response.email,
          createdAt: response.userCreatedAt,
          userHash: response.userHash,
          hideDefaultLauncher: isUserFlowFeatureAvailable,
          customAttributes: {
            'Desktop App version':
              localStorage.getItem(LocalStorageKeys.CREATORHERO_APP_VERSION) ??
              'Not using desktop app right now',
          },
        })
        const userData = accountInformationQuery.data

        if (!userData) {
          return
        }

        identify(userData)

        setSentryUserContext(userData.userId)
      },
      onError: (error: AxiosError) => {
        console.log('Intercom configuration error:', error)
      },
    },
  )

  const ownerQuery = useQuery(
    ['owner', accountInformationQuery.data?.userId],
    accountService.getCurrentOwner,
    {
      enabled:
        !accountInformationQuery.isFetching &&
        !!accountInformationQuery.data &&
        accountInformationQuery.data.memberRole === TeamMemberRole.LEADER,
      onError: (error) =>
        handleError(error, 'Something went wrong while fetching current owner'),
    },
  )

  const loginWithToken = useCallback((token: string) => {
    dispatchAccountAction({
      accessToken: token,
    })
    analytics.reset()
    window.location.reload()
  }, [])

  const loginMutation = useMutation(
    (loginData: AccountLoginRequest) => authService.login(loginData),
    {
      onSuccess: (response) => {
        const tfaEnabled = response['tfa-enabled']

        analytics.track(SegmentEvents.UserSignedIn)
        dispatchAccountAction({
          accessToken: response['auth-token'],
          isLoggedIn: !tfaEnabled,
          isManualLogin: !tfaEnabled,
        })
      },
    },
  )

  const registerMutation = useMutation(
    (registerData: AccountRegisterRequest) =>
      authService.register(registerData),
    {
      onSuccess: (response) => {
        dispatchAccountAction({
          accessToken: response['auth-token'],
          isManualLogin: true,
        })
      },
    },
  )

  const setAsLoggedIn = useCallback(() => {
    dispatchAccountAction({
      isLoggedIn: true,
      isManualLogin: true,
    })
  }, [])

  const setTfaEnabled = useCallback(
    (enabled: boolean) => {
      if (account == null) {
        return
      }

      const newAccount = {
        ...account,
        tfaEnabled: enabled,
      }

      setAccount(newAccount)
      dispatchAccountAction({
        account: newAccount,
      })
    },
    [account],
  )

  const registerAfterPaymentMutation = useMutation(
    authService.completeRegisterAfterPayment,
    {
      onSuccess: (response) => {
        dispatchAccountAction({
          accessToken: response['auth-token'],
        })
      },
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while registering. Please contact support.',
        )
      },
    },
  )

  const resetPasswordMailMutation = useMutation(
    (resetPasswordMailData: AccountResetPasswordMailRequest) =>
      passwordService.requestPasswordResetMail(resetPasswordMailData),
  )

  const resetPasswordMutation = useMutation(
    (resetPasswordData: ResetPasswordForm) =>
      PasswordResetRestControllerService.resetPassword({
        requestBody: resetPasswordData,
      }),
  )

  const activateAccountMutation = useMutation(
    (accountActivationRequest: AccountActivationRequest) =>
      authService.activateAccount(accountActivationRequest),
  )

  const logoutMutation = useMutation(authService.logout, {
    onSettled: () => {
      dispatchCustomEvent(EVENTS.LOGOUT)

      localStorage.removeItem(LocalStorageKeys.ACCOUNTS)
      onLogout()
      setAccount(null)
      dispatchAccountAction({
        isLoggedIn: false,
        accessToken: null,
        account: null,
        isManualLogin: false,
      })
    },
  })

  const updateSettingsMutation = useMutation(
    'settings-update',
    (requestBody: UpdateUserSettingsRequest) => {
      return UserSettingsService.updateSettingsDeprecated({ requestBody })
    },
    {
      onSuccess: (data) => {
        setCurrentTheme(data.theme!)
        dispatchAccountAction({
          userSettings: data,
        })
      },
    },
  )

  const updateOwnerMutation = useMutation(
    'owner-update',
    OwnerRestControllerService.updateOwnerInformation,
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'owner',
          accountInformationQuery.data?.userId,
        ])
      },
    },
  )

  const logoutHandler = useCallback(() => {
    logoutMutation.mutateAsync().then(() => {
      queryClient.invalidateQueries()
    })
  }, [logoutMutation, queryClient])

  const updateSettingsHandler = useCallback(
    (settings: UpdateUserSettingsRequest, onSuccess?: () => void) => {
      updateSettingsMutation.mutateAsync(settings).then(() => {
        if (onSuccess) {
          onSuccess()
        }
      })
    },
    [updateSettingsMutation],
  )

  const updateOwnerHandler = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (requestBody: Record<string, any>) => {
      updateOwnerMutation.mutateAsync({ requestBody })
    },
    [updateOwnerMutation],
  )

  const registerAfterPaymentHandler = useCallback(
    async (registerData: AccountRegisterAfterPaymentRequest) => {
      const res = await registerAfterPaymentMutation.mutateAsync(registerData)

      return res
    },
    [registerAfterPaymentMutation],
  )

  useEffect(
    () => {
      accountInformationQuery.refetch()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isManualLogin],
  )

  const values: AccountContext = useMemo(
    () => ({
      agencyLogoCurrent: currentLogo,
      login: loginMutation,
      loginWithToken,
      setAsLoggedIn: setAsLoggedIn,
      register: registerMutation,
      resetPasswordMail: resetPasswordMailMutation,
      resetPassword: resetPasswordMutation,
      activateAccount: activateAccountMutation,
      owner: ownerQuery.data,
      updateOwner: updateOwnerHandler,
      error,
      isLoggedIn,
      checkedAuthentication,
      accessToken,
      account,
      isAccountLoaded,
      isManualLogin,
      userSettings,
      updateUserSettings: updateSettingsHandler,
      themeData,
      registerAfterPayment: registerAfterPaymentHandler,
      logout: logoutHandler,
      refetchAccount: () => {
        accountInformationQuery.refetch()
      },
      setTfaEnabled: setTfaEnabled,
    }),
    [
      currentLogo,
      loginMutation,
      setAsLoggedIn,
      loginWithToken,
      registerMutation,
      resetPasswordMailMutation,
      resetPasswordMutation,
      activateAccountMutation,
      ownerQuery.data,
      updateOwnerHandler,
      error,
      isLoggedIn,
      checkedAuthentication,
      accessToken,
      account,
      isAccountLoaded,
      isManualLogin,
      userSettings,
      updateSettingsHandler,
      themeData,
      registerAfterPaymentHandler,
      logoutHandler,
      setTfaEnabled,
      accountInformationQuery,
    ],
  )

  return (
    <AccountStateContext.Provider value={values}>
      {children}
    </AccountStateContext.Provider>
  )
}
